const drawerEvents = {
    OnLineItemUpdate: 'action:cart:updateLineItem',
    onQuantityChange: 'on:QuantityChange',
    triggerCartChange: 'action:cart:updateCart',
};

class DrawerCart extends HTMLElement {
    constructor() {
        super();
        this.currentLineItem;
        this.lineItemError = null;
        this.cartIdentifier = this.getAttribute('render-section');
        this.checkoutContainer = document.querySelector(
            '[cart-checkout-container]'
        );

        this.sectionId = this.getAttribute('data-sectionId') || this.cartIdentifier;

        this.addEventListener(_EVENT_HELPER.OnLineItemUpdate,this.handleLineItem.bind(this) );

        /* Listening for this event to update cart items and show cart drawer base on products Added to cart via product-form,product-card component 
            Usage: document.dispatchEvent(new CustomEvent(_EVENT_HELPER.triggerCartChange, { detail: {}}));
            Note: Make sure to pass the `detail` @object with the data consisting of sections that can fetched via section rendering Api functionality of Shopify
        */

        document.addEventListener(_EVENT_HELPER.triggerCartChange,this.renderCart.bind(this));

        /* Listening for this event to update cart items and show cart drawer based on custom functionality of the theme 
            Scenarios: When user adds a product to cart, the cart drawer should be updated and shown
            Usage: document.dispatchEvent(new CustomEvent(_EVENT_HELPER.updateAndShowCart, { detail: {}}));
        */
        document.addEventListener(_EVENT_HELPER.updateAndShowCart, this.fetchAndRenderCartItems.bind(this));

        document.addEventListener(_EVENT_HELPER.showCartPopup, (e) => {
            if (!e?.detail?.sections) return;
            this.renderCartContents(e.detail);
            this._updateCartItemCount(e.detail);
        });

        this._formSubmitHandler = this.formSubmitHandler.bind(this);
        // Checkout lives in a nested form, not #cart-form — listen on the drawer
        this.addEventListener('submit', (e) => {
            const isCheckout =
                e.submitter?.getAttribute('name') === 'checkout' ||
                (e.target.querySelector?.('[name="checkout"]') && !e.target.querySelector('[name="id"]'));
            if (isCheckout) this._formSubmitHandler(e);
        });
    }

    async formSubmitHandler(event) {
        event.preventDefault();

        if (this._submitting) return;
        this._submitting = true;

        const btn =
            event.submitter?.getAttribute('name') === 'checkout'
                ? event.submitter
                : this.querySelector('[name="checkout"]');
        if (typeof HELPER_UTIL !== 'undefined' && HELPER_UTIL.submitButtonState && btn) {
            HELPER_UTIL.submitButtonState(btn).loadingState();
        } else if (btn) {
            btn.classList.add('loadingState');
            btn.disabled = true;
        }

        let navigated = false;
        const goToCheckout = () => {
            if (navigated) return;
            navigated = true;
            window.location.href = '/checkout';
        };

        const startedAt = Date.now();
        const navigationCeiling = setTimeout(goToCheckout, 1500);
        const shippingTest = window.verifyTest?.shipping_version || '';

        // shipping_test is analytics metadata — never block checkout on it
        if (shippingTest) {
            try {
                const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
                const abortTimer = controller ? setTimeout(() => controller.abort(), 1500) : null;

                await fetch('/cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        attributes: {
                            shipping_test: shippingTest,
                        },
                    }),
                    ...(controller ? { signal: controller.signal } : {}),
                });

                if (abortTimer) clearTimeout(abortTimer);
            } catch (e) {
                // analytics attribute only — never block the order
            }
        }

        // Give the spinner one paint so the tap is visible before we leave
        const spinnerMs = 400 - (Date.now() - startedAt);
        if (spinnerMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, spinnerMs));
        }

        clearTimeout(navigationCeiling);
        goToCheckout();
    }

    formSetEvent() {
        this.form = this.querySelector('#cart-form');

        this.form?.removeEventListener('submit', this._formSubmitHandler)
        this.form?.addEventListener('submit', this._formSubmitHandler)
    }

    connectedCallback() {
        this.formSetEvent()
        this.removeUnlistedProducts()
    }

    getUnlistedProducts() {
        const node = this.querySelector('#cart-drawer-unlisted-products');
        if (!node) return [];
        try {
            const items = JSON.parse(node.textContent);
            return Array.isArray(items) ? items.filter((item) => item?.key) : [];
        } catch (e) {
            return [];
        }
    }

    removeUnlistedProducts() {
        if (this._removingUnlisted) return;

        const items = this.getUnlistedProducts();
        if (!items.length) return;

        this._removingUnlisted = true;
        const updates = {};
        items.forEach((item) => {
            updates[item.key] = 0;
        });

        const omit = this.cartIdentifier === 'cart-drawer'
            ? ['cart-popup', 'manual-discount-codes']
            : ['cart-popup'];

        HELPER_UTIL.fetchData({
            method: 'POST',
            url: `${window.Shopify.routes.root}cart/update.js`,
            data: JSON.stringify({ updates }),
            config: {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
            onsuccess: () => {
                HELPER_UTIL.fetchCartSections(this.cartIdentifier, { omit })
                    .then((sections) => {
                        this.renderCartContents({ sections });
                        this.renderElementsBasedOnCartItems({ sections });
                    })
                    .catch(() => this.fetchAndRenderCartItems());
            },
            onerror: (error) => {
                console.log(error);
            },
            defaultCb: () => {
                this._removingUnlisted = false;
            },
        });
    }

    _getCartItemCount(data) {
        const cartSection = data?.sections?.[this.cartIdentifier];
        if (!cartSection) return this.dataset.cartitemcount ?? 0;

        const parseCartSectionHTML = new DOMParser().parseFromString(
            cartSection,
            'text/html'
        );

        return parseCartSectionHTML.querySelector(this.tagName)?.dataset
            .cartitemcount ?? this.dataset.cartitemcount ?? 0;
    }

    renderCart(e) {
        if (!e?.detail?.sections) return;

        this.renderCartContents(e.detail);
        // Update dependent Elements to be render
        this.renderElementsBasedOnCartItems(e.detail);
        if (this.cartIdentifier == 'cart-drawer') {
            const drawer = this.closest('cart-drawer-component');
            if (drawer && !drawer.isDrawerOpen) {
                HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.showDrawer, this, {drawerType: 'cart-drawer',});
            }
        }
    }

    _get(property) {
        return this[property];
    }

    _set(property, value) {
        this[property] = value;
    }

    _updateCartItemCount(data) {
        this.cartItemCount = this._getCartItemCount(data);
        this.dataset.cartitemcount = this.cartItemCount;
    }

    showDrawerToastMessage({ responseObj, messageType }) {
        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.triggerToastMessage,
            document,
            {
                toastType: 'common-errors',
                messageType: messageType,
                message: responseObj.message,
            }
        );
    }

    quantityElementAction({ line, quantity }) {
        const enable = () => {
            this.querySelectorAll('quantity-selector').forEach((el) =>
                el.setAttribute('is-disabled', 'false')
            );
        };

        const disable = () => {
            this.querySelectorAll('quantity-selector').forEach((el) =>
                el.setAttribute('is-disabled', 'true')
            );
        };

        const resetToPreviousValue = () => {
            this.querySelector(`[item-index="${line}"]`)
                .querySelectorAll('quantity-selector input')
                .forEach((el) => {
                    el.value = (Number(quantity) - 1).toString();
                });
        };

        return {
            enable,
            disable,
            resetToPreviousValue,
        };
    }

    updateCartTotal(data) {
        const cartTotal = data.total_price;
        const cartTotalEl = document.querySelectorAll('[cart-total]');
        [...cartTotalEl].forEach((el) => {
            el.textContent = Shopify.formatMoney(
                cartTotal,
                Shopify.money_format
            );
        });
    }

    cartCheckoutEl() {
        // const checkoutContainer = document.querySelector(
        //     '[cart-checkout-container]'
        // );
        const hideContainerClass = 'hide';
        const show = () => {
            if (this.checkoutContainer.classList.contains(hideContainerClass)) {
                this.checkoutContainer.classList.remove(hideContainerClass);
            }
        };
        const hide = () => {
            this.checkoutContainer.classList.add(hideContainerClass);
        };

        return {
            show,
            hide,
        };
    }

    renderElementsBasedOnCartItems(data) {
        this._updateCartItemCount(data);
        if (this.cartItemCount > 0) {
            HELPER_UTIL.dispatchCustomEvent(
                _EVENT_HELPER.showCartRecommendations,
                this
            );
        } else {
            HELPER_UTIL.dispatchCustomEvent(
                _EVENT_HELPER.hideCartRecommendations,
                this
            );
        }
    }

    handleLineItem({ detail }) {
        // Make Api request to update cart drawer based on line item index
        /* Detail Object return data inform of @object as follows:
            {
            line: line Item index,
            quantity: Line item quantity
         } */
        const data = {
            id: detail.key,
            quantity: detail.quantity,
        };

        // Disable quantity selector of which quantity was increased
        this.quantityElementAction(detail).disable();

        HELPER_UTIL.fetchData({
            method: 'POST',
            url: `${window.Shopify.routes.root}cart/change.js`,
            data: JSON.stringify(data),
            config: {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
            onsuccess: () => {
                const omit = this.cartIdentifier === 'cart-drawer'
                    ? ['cart-popup', 'manual-discount-codes']
                    : ['cart-popup'];
                HELPER_UTIL.fetchCartSections(this.cartIdentifier, { omit })
                    .then((sections) => {
                        HELPER_UTIL.dispatchCustomEvent(
                            _EVENT_HELPER.triggerCartChange,
                            this,
                            { sections }
                        );
                    })
                    .catch(() => this.fetchAndRenderCartItems());
            },
            onerror: (error) => {
                if (error.json) {
                    error.json().then((errorObj) => {
                        if (errorObj.status === 422) {
                            HELPER_UTIL.dispatchCustomEvent(
                                _EVENT_HELPER.triggerlineItemError,
                                this,
                                {
                                    errorObj: {
                                        lineItem: detail.key,
                                        responseObj: errorObj,
                                        messageType: 'error',
                                    },
                                }
                            );
                        }
                    });
                } else {
                    this.showDrawerToastMessage({
                        responseObj: {
                            message: 'Something went wrong.Please try again',
                        },
                        messageType: 'error',
                    });
                }
            },
            defaultCb: () => {
                this.quantityElementAction(detail).enable();
            },
        });
    }

    renderCartContents({ sections, source = undefined }) {
        if (!sections) return;
        try {
            const getSectionsToLoop = HELPER_UTIL.sectionRenderUtil(this.cartIdentifier).getSectionsObject();
            getSectionsToLoop.forEach((sectionsObjItem) => {
                // Here sections{@object} is the data from the AJax ca;l we made to cart
                // sectionsObjItem @{object} is the class property consisting list of sections and there ids
                if (!!sections[sectionsObjItem.sectionId]) {
                    [...document.querySelectorAll(sectionsObjItem.queryElement)].forEach((section) => {
                        const incoming = HELPER_UTIL.getHTMLFromString(sections[sectionsObjItem.sectionId],sectionsObjItem.queryElement);
                        if (!incoming) return;

                        // Keep already-loaded recs so adding a rec does not rebuild the carousel
                        const keepRecs = section.matches?.('drawer-cart')
                            ? section.querySelector('[cart-recs-root]')
                            : null;
                        if (keepRecs) keepRecs.remove();

                        section.innerHTML = incoming.innerHTML;

                        if (keepRecs) {
                            const placeholder = section.querySelector('[cart-recs-root]');
                            if (placeholder) placeholder.replaceWith(keepRecs);
                        }
                    });
                }
            });
            if(source == 'plan-switch') {
                const totalSavings = document.querySelector('.total-savings');
                totalSavings&&totalSavings.classList.add('total-savings--switch')
            }
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    document.dispatchEvent(new CustomEvent("cart:rendered"));
                });
            });

            this.formSetEvent()
            this.removeUnlistedProducts()
        } catch (error) {
            console.log(error);
        }
    }

    fetchAndRenderCartItems() {
        const omit = this.cartIdentifier === 'cart-drawer'
            ? ['cart-popup', 'manual-discount-codes']
            : ['cart-popup'];
        const sectionsToRender = HELPER_UTIL.sectionRenderUtil(this.cartIdentifier).getSectionsString({ omit });
        // Section Rendering API returns { [sectionId]: html } — not { sections: ... }
        HELPER_UTIL.fetchData({
            method: 'GET',
            url: `${window.Shopify.routes.root}?sections=${sectionsToRender}`,
            onsuccess: (data) => {
                if (!data || typeof data !== 'object') return;
                HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.triggerCartChange, this, { sections: data });
            },
            onerror: (error) => {
                console.log(error);
            },
        });
    }

    disconnectedCallback() {
        // Remove all the event listeners added inside constructor to prevent memory leaks
        this.removeEventListener(
            drawerEvents.OnLineItemUpdate,
            this.handleLineItem.bind(this)
        );
        this.removeEventListener(
            drawerEvents.onQuantityChange,
            this.handleLineItem.bind(this)
        );
        this.removeEventListener(
            drawerEvents.triggerCartChange,
            this.renderCart.bind(this)
        );
        this.removeEventListener(
            drawerEvents.updateAndShowCart,
            this.fetchAndRenderCartItems.bind(this)
        );
    }
}

customElements.get('drawer-cart') ||
    customElements.define('drawer-cart', DrawerCart);

class CartPage extends DrawerCart {
    constructor() {
        super();
    }

    renderElementsBasedOnCartItems(data) {
        const cartItemCount = this._getCartItemCount(data);
        if (cartItemCount > 0) {
            // this.updateCartTotal(data);
            if (this.checkoutContainer) {
                this.cartCheckoutEl().show();
            }
        } else {
            if (this.checkoutContainer) {
                this.cartCheckoutEl().hide();
            }
        }
    }

    showDrawerToastMessage({ responseObj, messageType }) {
        const toastEl = this.querySelector('toast-message');
        toastEl.toast({
            message: responseObj.message,
            messageType: messageType,
        });
    }
}

customElements.get('cart-page') || customElements.define('cart-page', CartPage);

class DrawerItem extends HTMLElement {
    constructor() {
        super();
        this.itemKey = this.getAttribute('item-key'); // Unique line item key of the product in cart
        this.removeItemElement = this.querySelectorAll('[remove-lineItem]');
        [...this.removeItemElement].forEach((el) => {
            el.addEventListener(
                'click',
                this.dispatchLineItemUpdate.bind(this)
            );

            el.addEventListener('keyup', (e) => {
                if (e.key === 'Enter') {
                    this.dispatchLineItemUpdate();
                }
            });
        });
        this.addEventListener(
            _EVENT_HELPER.onQuantityChange,
            this.dispatchLineItemUpdate.bind(this)
        );

        document.addEventListener(
            _EVENT_HELPER.triggerlineItemError,
            this.showLineItemError.bind(this)
        );
    }

    dispatchLineItemUpdate(e) {
        const productQuantity = e?.detail.quantity;
        let detail = {
            key: this.itemKey,
            quantity: productQuantity ? productQuantity : '0',
        };

        if (detail.quantity === '0' && e?.target.closest("[animation-slide-left]")) {
            Motion.animate(e?.target.closest("[animation-slide-left]"),
                {
                    x: 40,
                    opacity: 0
                },
                {
                    duration: parseFloat(0.3),
                    ease: "cubic-bezier(.75,0,.175,1)",
                })
        }
        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.OnLineItemUpdate,
            this,
            detail
        );
    }

    handleLineItemErrorUpdate(errorObj) {
        let customMessageComponent =
            this.querySelector('custom-message') ||
            this.nextElementSibling.querySelector('custom-message');
        console.log(errorObj);
        // Adding  a
        // setTimeout(() => {
        customMessageComponent.showCustomMessage({
            errorMessage: errorObj.responseObj.message,
            messageType: errorObj.messageType,
        });
        // }, 0);
    }

    resetQuantitySelector() {
        this.querySelector('quantity-selector input').value =
            this.getAttribute('item-quantity');
    }

    showLineItemError(event) {
        const { errorObj } = event.detail;
        if (errorObj && errorObj.lineItem === this.itemKey) {
            this.handleLineItemErrorUpdate(errorObj);
            this.resetQuantitySelector();
        }
    }

    disconnectedCallback() {
        // Remove all the event listeners added inside constructor to prevent memory leaks
        this.removeEventListener(
            _EVENT_HELPER.onQuantityChange,
            this.dispatchLineItemUpdate.bind(this)
        );

        [...this.removeItemElement].forEach((el) => {
            el.removeEventListener(
                'click',
                this.dispatchLineItemUpdate.bind(this)
            );
            el.removeEventListener('keyup', (e) => {
                if (e.key === 'Enter') {
                    this.dispatchLineItemUpdate();
                }
            });
        });
    }
}

customElements.get('drawer-item') ||
    customElements.define('drawer-item', DrawerItem);



//More checkout button animation 

class AdditionalCheckoutButtonsAnimation extends HTMLElement {
    constructor() {
        super();
        this.toggleButton = this.querySelector("[toggle-payment-buttons]")
        this.toggleButton?.addEventListener("click", this.togglePaymentButtons.bind(this))
        this.buttonsWrapper = this.querySelector("[checkout-buttons-wrapper]")

    }

    togglePaymentButtons(e) {
        e.preventDefault()
        this.toggleButton.classList.toggle("show-payment-options")
        this.setAttribute("style", `--button-wrapper-height:${this.buttonsWrapper?.firstElementChild.offsetHeight}px`)
        this.buttonsWrapper?.classList.toggle("active")
    }

}

customElements.get('additional-checkout-buttons-animation') ||
    customElements.define("additional-checkout-buttons-animation", AdditionalCheckoutButtonsAnimation)

class SellingPlanSwitch extends HTMLElement {
    connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.infoTrigger = this.querySelector('.selling-plan-switch__info');
        this.infoContent = this.querySelector('.selling-plan-switch__content');
        if (this.infoTrigger && this.infoContent) {
            this.onInfoToggleHandler = this.onInfoToggle.bind(this);
            this.infoTrigger.addEventListener('click', this.onInfoToggleHandler);
            this.infoTrigger.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    this.onInfoToggle(event);
                }
            });
        }

        this.productFormButton = this.querySelector('.selling-plan-switch__form-button');
        if (!this.productFormButton) return;
        this.onFormSubmitHandler = this.onFormSubmit.bind(this);
        this.productFormButton.addEventListener('click', this.onFormSubmitHandler);
    }

    onInfoToggle(event) {
        event.preventDefault();
        const isExpanded = this.infoContent.getAttribute('aria-hidden') === 'false';
        const nextExpanded = !isExpanded;
        const label = nextExpanded
            ? this.infoTrigger.dataset.labelLess
            : this.infoTrigger.dataset.labelMore;

        this.infoContent.setAttribute('aria-hidden', nextExpanded ? 'false' : 'true');
        this.infoTrigger.setAttribute('aria-expanded', String(nextExpanded));
        this.infoTrigger.setAttribute('aria-label', label);
        this.infoTrigger.textContent = label;
    }

    onFormSubmit(event) {
        event.stopImmediatePropagation();
        event.preventDefault();

        const sellingPlanId = this.dataset.sellingPlanId;
        const line = this.dataset.line;
        const quantity = this.dataset.quantity || '1';
        const cartType = this.dataset.cartType || 'cart-drawer';
        if (!sellingPlanId || !line) return;

        this.productFormButton.classList.add('loadingState');
        this.productFormButton.disabled = true;
        this.productFormButton.removeEventListener('click', this.onFormSubmitHandler);

        const sectionsToRender = HELPER_UTIL.sectionRenderUtil(cartType).getSectionsString();

        fetch(`${window.Shopify.routes.root}cart/change.js?sections=${sectionsToRender}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({
                line: Number(line),
                quantity: Number(quantity),
                selling_plan: Number(sellingPlanId),
            }),
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.status && Number(data.status) >= 400) {
                    throw data;
                }

                const finish = (payload) => {
                    HELPER_UTIL.submitButtonState(this.productFormButton).completeState();
                    HELPER_UTIL.dispatchCustomEvent(
                        _EVENT_HELPER.triggerCartChange,
                        document,
                        {
                            sections: payload.sections,
                            source: 'plan-switch',
                        }
                    );
                };

                if (data.sections) {
                    finish(data);
                    this.refreshCartAfterFreebie(cartType, data.item_count);
                    return;
                }

                return fetch(`${window.Shopify.routes.root}?sections=${sectionsToRender}`)
                    .then((sectionsResponse) => sectionsResponse.json())
                    .then((sectionsData) => {
                        data.sections = sectionsData;
                        finish(data);
                        this.refreshCartAfterFreebie(cartType, data.item_count);
                    });
            })
            .catch(() => {
                this.productFormButton.disabled = false;
                this.productFormButton.classList.remove('loadingState');
                this.productFormButton.addEventListener('click', this.onFormSubmitHandler);
            });
    }

    refreshCartAfterFreebie(cartType, itemCountBeforeFreebie) {
        const sectionsToRender = HELPER_UTIL.sectionRenderUtil(cartType).getSectionsString();
        window.setTimeout(() => {
            fetch(`${window.Shopify.routes.root}cart.js`)
                .then((response) => response.json())
                .then((cart) => {
                    if (
                        itemCountBeforeFreebie != null &&
                        cart.item_count === itemCountBeforeFreebie
                    ) {
                        return null;
                    }
                    return fetch(`${window.Shopify.routes.root}?sections=${sectionsToRender}`).then(
                        (response) => response.json()
                    );
                })
                .then((sections) => {
                    if (!sections) return;
                    HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.triggerCartChange, document, {
                        sections,
                        source: 'plan-switch',
                    });
                })
                .catch(() => {});
        }, 800);
    }
}

customElements.get('selling-plan-switch') ||
    customElements.define("selling-plan-switch", SellingPlanSwitch);

document.addEventListener('cart:rendered', () => {
    const cartDrawerComponent = document.querySelector('cart-drawer-component');
    if (cartDrawerComponent && typeof cartDrawerComponent.setDyanmicHeight === 'function') {
        requestAnimationFrame(() => {
            cartDrawerComponent.setDyanmicHeight();
        });
    }
});